import { useState } from 'react';
import { Save, X } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { reportTemplatesApi, TEMPLATE_CATEGORIES, type Template } from '../lib/reportTemplatesApi';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (template: Template) => void;
}

/** Leere Vorlage anlegen (Name/Beschreibung/Kategorie) — Bullets folgen danach im TemplateEditModal. */
export default function CreateTemplateModal({ open, onClose, onCreated }: Props) {
  const { darkMode } = useDarkMode();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const reset = () => {
    setName('');
    setDescription('');
    setCategory('');
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await reportTemplatesApi.create({
        name: name.trim(),
        description: description.trim(),
        category: category || null,
        items: [],
      });
      reset();
      onCreated(result);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erstellen fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const card = darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200';
  const input = darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={`rounded-xl border shadow-xl p-6 max-w-md w-full ${card}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Neue Vorlage</h2>
          <button onClick={handleClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="new-tpl-name" className="block text-sm font-medium mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="new-tpl-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${input}`}
              placeholder="z.B. BAFA Standard-Digital"
            />
          </div>

          <div>
            <label htmlFor="new-tpl-desc" className="block text-sm font-medium mb-1">
              Beschreibung (optional)
            </label>
            <textarea
              id="new-tpl-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              rows={3}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y ${input}`}
            />
          </div>

          <div>
            <label htmlFor="new-tpl-category" className="block text-sm font-medium mb-1">
              Kategorie (optional)
            </label>
            <select
              id="new-tpl-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg text-sm ${input}`}
            >
              <option value="">Ohne Kategorie</option>
              {TEMPLATE_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {error && (
            <div className="p-2 bg-red-100 border border-red-300 text-red-700 rounded text-sm">{error}</div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={handleClose}
            className={`px-4 py-2 text-sm rounded-lg border ${darkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={name.trim().length === 0 || saving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Erstellt…' : 'Erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
}
